import React from "react";

import { Link } from "@/i18n/routing";
import { mapCtaPosition, mapCtaStyle } from "@/lib/banners/mapCtaStyle";
import { PublicBannerItem } from "@/lib/banners/types";
import { cn } from "@/lib/utils";
import { useLocale } from "next-intl";

type BannerCtaProps = {
  banner: PublicBannerItem;
  className?: string;
  labelFallback?: string;
  hrefFallback?: string;
  /** When set, the button opens this handler instead of navigating to the CTA link. */
  onClick?: () => void;
};

const BannerCta = ({ banner, className, labelFallback, hrefFallback, onClick }: BannerCtaProps) => {
  const ctaConfig = banner.ctaConfig;
  const label = ctaConfig?.label || labelFallback;
  const href = ctaConfig?.href || hrefFallback;
  const locale = useLocale();

  const isRTL = locale === "ar";

  if (!ctaConfig?.enabled || !label || (!href && !onClick)) {
    return null;
  }

  const isExternalHref = /^https?:\/\//.test(href ?? "");
  const { className: ctaClassName, style: ctaStyle } = mapCtaStyle(ctaConfig);
  const { className: positionClassName, style: positionStyle } = mapCtaPosition(ctaConfig, isRTL);

  const sharedClassName = cn(
    "absolute z-20 !rounded-xl text-center font-urbanist transition-all duration-300",
    ctaClassName,
    positionClassName,
    className,
  );

  const mergedStyle = {
    ...positionStyle,
    ...ctaStyle,
  };

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={cn(sharedClassName, "cursor-pointer")} style={mergedStyle}>
        {label}
      </button>
    );
  }

  if (!href) {
    return null;
  }

  if (isExternalHref || ctaConfig.openInNewTab) {
    return (
      <a
        href={href}
        target={ctaConfig.openInNewTab ? "_blank" : undefined}
        rel={ctaConfig.openInNewTab ? "noopener noreferrer" : undefined}
        className={sharedClassName}
        style={mergedStyle}
      >
        {label}
      </a>
    );
  }

  return (
    <Link href={href as any} className={sharedClassName} style={mergedStyle}>
      {label}
    </Link>
  );
};

export default BannerCta;
